import { BookingCustomer } from "./BookingTypes";

export interface ObjectBase {
  Alias: string;
  City: string;
  Commons: ObjectBaseCommons;
  Region: string;
  Name: string;
  OnlineBooking: boolean;
  OnlineOffer: boolean;
  OnlineRequest: boolean;
  DisableOnlinePriceCalculation: boolean;
  Position: string;
  Postal: string;
  Street: string;
  maxAgeExtraBed1: number;
  maxAgeExtraBed2: number;
  maximumPersons: number;
  minimumPersons: number;
  numberOfBathrooms: number;
  numberOfBedrooms: number;
  numberOfPets: number;
  numberOfRooms: number;
  shortIdentifier: string;
  showMap: boolean;
  sizeQm: string;
  uniqueId: string;
  zoomLevel: string;
}

export interface ObjectFeature {
  Category: string;
  Content?: string;
  FieldType: 'STATIC' | 'DYNAMIC';
  MarkType: string;
  Title: string;
  uniqueId: string;
}
export interface ObjectImage {
  isDefault: boolean;
  url: string;
}

export enum SALES_TYPE {
  PET = 'PET',
  RENTAL = 'RENTAL',
  SERVICE = 'SERVICE',
  TOURIST_TAX = 'TOURIST_TAX',
  DEPOSIT = 'DEPOSIT',
  FINAL_CLEANING = 'FINAL_CLEANING',
  EXTRA_SERVICE = 'EXTRA_SERVICE',
  BILLING = 'BILLING',
}

export enum ServiceFieldType {
  PER_DAY_DURATION = 'perDayDuration',
  FLAT_RATE = 'flatRate',
  PER_DAY = 'perDay',
  UNIT = 'unit',
  FLAT_RATE_DURATION_ADULT = 'flatRateDurationAdult',
  FLAT_RATE_PET = 'flatRatePet',
}

export interface ObjectSalesType {
  Price: number;
  SalesType: SALES_TYPE;
  SalesTypeLabel: string;
  additionalInfo: ObjectAdditionalInfo[];
  totalPrice: number;
}

export interface ObjectRentalSalesType {
  SalesType: SALES_TYPE.RENTAL;
  SalesTypeLabel: string;
  additionalInfo: Array<ObjectAdditionalInfo>;
  lowestPriceSearch: boolean;
  numberOfDays: number;
  priceCorrectionsInfo: { personData: any[]; bookingData: any[] };
  services: any[];
  totalPrice: number;
}

export interface ObjectAdvertisingData {
  dayPrice: number;
  numberOfDays: number;
  numberOfPersons: number;
}

export interface ObjectCommonSalesType {
  depositDate: string;
  depositPrice: number;
  finalPaymentDate: string;
  finalPaymentPrice: number;
  finalReservationDate: string;
  partialPaymentDate: string;
  partialPaymentPrice: number;
  processesFeeBookingChange: number;
  totalPrice: number;
  advertisingData: ObjectAdvertisingData;
}

export interface ObjectDepositSalesType {
  SalesType: SALES_TYPE;
  SalesTypeLabel: string;
  additionalInfo: Array<ObjectAdditionalInfo>;
  totalPrice: number;
  PaymentMethod: string;
  Vat: number;
}

export interface ObjectFinalCleaningSalesType {
  SalesType: SALES_TYPE;
  SalesTypeLabel: string;
  additionalInfo: Array<ObjectAdditionalInfo>;
  totalPrice: number;
  PaymentMethod: string;
  Vat: number;
}

export interface ObjectTouristTaxSalesType {
  SalesType: SALES_TYPE;
  SalesTypeLabel: string;
  totalPrice: number;
  additionalInfo: Array<ObjectAdditionalInfo>;
  services: any[];
}

export interface ObjectPetSalesType {
  SalesType: SALES_TYPE;
  additionalInfo: Array<ObjectAdditionalInfo>;
  SalesTypeLabel: string;
  subTotalPrice: number;
  subTotalUnitPetPrice: number;
  PaymentMethod: string;
  Vat: number;
  totalPrice: number;
  services: any[];
}

export interface ObjectAdditionalInfo {
  PaymentMethod: string;
  Title: string;
  from: string;
  to: string;
  numberOfDays: number;
  subTotalPetPrice?: 0;
  subTotalPrice: number;
  subTotalUnitPetPrice?: number;
  subTotalUnitPrice: number;
  Vat: number;
}

export interface ObjectPrice {
  RENTAL: ObjectRentalSalesType;
  COMMON: ObjectCommonSalesType;
  DEPOSIT?: ObjectDepositSalesType;
  FINAL_CLEANING?: ObjectFinalCleaningSalesType;
  PET?: ObjectPetSalesType;
  SERVICE?: ObjectSalesType;
  TOURIST_TAX?: ObjectTouristTaxSalesType;
  EXTRA_SERVICE?: ObjectSalesType;
  BILLING?: ObjectSalesType;
}

export interface ObjectService {
  Description: string;
  FieldType: ServiceFieldType;
  Mandatory: boolean;
  Price: number;
  SalesType: SALES_TYPE;
  Title: string;
  endDate: string;
  includedInPrice: boolean;
  maximumNumber: number;
  startDate: string;
  uniqueId: string;
}

export interface ObjectText {
  Content: string;
  Language: string;
  MarkType: string;
  Title: string;
  Type: string;
}
export interface ObjectSeasonPrice {
  Title: string;
  from: string;
  maxPersons: number;
  minimumLength: number;
  to: string;
  totalPrice: number;
  arrivalDays: string[];
  departureDays: string[];
}

export interface ObjectVacationDate {
  fromDate: string;
  toDate: string;
}

export interface SingleObject {
  baseData: ObjectBase;
  featuresData: Array<ObjectFeature>;
  imagesData: Array<ObjectImage>;
  priceData?: ObjectPrice;
  servicesData: Array<ObjectService>;
  textsData: Array<ObjectText>;
  seasonsPriceData?: Array<ObjectSeasonPrice>;
  language?: { active: string; availableLanguages: string[] };
  vacationsData?: ObjectVacationDate[];
}

export interface ObjectCity {
  Region: string;
  Title: string;
  quantity: number;
  uniqueId: string;
}

export interface ObjectCountry {
  Title: string;
  quantity: number;
  uniqueId: string;
}

export interface ObjectRegion {
  Country: string;
  Title: string;
  quantity: number;
  uniqueId: string;
}
export interface ObjectLanguage {
  active: string;
  availableLanguages: Array<string>;
}

export interface ObjectResult {
  response: Array<BookingCustomer>;
  bookings: SingleObject;
  language: ObjectLanguage;
}

export interface ObjectData {
  module: string;
  result: ObjectResult;
}

export interface ObjectResponse {
  alpdesk_token: string;
  data: ObjectData;
  plugin: 'booking';
  username: 'widget';
}

export interface GroupedFeatures {
  category: string;
  features: Array<ObjectFeature>;
}

export interface ObjectBaseCommons {
  linkImprint: string;
  linkLegalNotice: string;
  linkTerms: string;
}
